import type { CustomToolTestResult, CustomToolView } from "../custom-tool-types";
import type { KbHitView, KbListView, KbPagesView, KbSourceView } from "../kb-types";
import type { KbFixProposalView } from "../kb-fix-types";
import type { ByokKeyView } from "../byok-types";
import type { FaqCandidateView, FaqMiningReportView } from "../faq-mining-types";
import type { WeeklyReportList, WeeklyReportSetting } from "../weekly-report-types";
import type { SmsFallbackSettingsView } from "../sms-fallback-types";
import type {
  AiAgentChecklist,
  AiAgentRunView,
  AiAgentProbeRun,
  AiMapStatsView,
  AiTraceStep,
  AiEvalCasesView,
  AiEvalImportResult,
  AiEvalCase,
  AiEvalRunView,
  AiAgentTestReply,
  AiStatusView,
  AiProviderModelsView,
  AiAgentView,
  AiProfileTemplatePreview,
} from "../automation-types";
import { toSitemapUrl } from "../sitemap-url";
import type {
  BuilderConversationView,
  BuilderProposalView,
  BuilderReplyView,
  BuilderTranscriptView,
} from "../workspace-copilot-types";
import type {
  OwnerAssistantActionResult,
  OwnerAssistantReply,
  OwnerAssistantSuggestion,
} from "../owner-assistant-types";
import type {
  AiLinkDomains,
  ApiClient,
  ReplyRateLimitSettings,
  VoiceTranscriptionSettings,
} from "../api-client";
import type { HttpTransport } from "../http/transport";

export type AiKnowledgeApiMethods = Pick<
  ApiClient,
  | "listKnowledgeSources"
  | "listCustomTools"
  | "createCustomTool"
  | "updateCustomTool"
  | "deleteCustomTool"
  | "testCustomTool"
  | "listAgentActions"
  | "markMessageWrong"
  | "getSmsFallbackSettings"
  | "saveSmsFallbackSettings"
  | "listByokKeys"
  | "addByokKey"
  | "testByokKey"
  | "setByokModel"
  | "rotateByokKey"
  | "revokeByokKey"
  | "listKbFixProposals"
  | "listFaqCandidates"
  | "approveFaqCandidate"
  | "rejectFaqCandidate"
  | "getFaqMiningReport"
  | "getWeeklyReports"
  | "saveWeeklyReportSetting"
  | "decideKbFixProposal"
  | "createKnowledgeSource"
  | "crawlWebsite"
  | "uploadKnowledgeFile"
  | "reindexKnowledgeSource"
  | "deleteKnowledgeSource"
  | "createWebsiteSource"
  | "listWebsitePages"
  | "recrawlWebsiteSource"
  | "updateKnowledgeSource"
  | "searchKnowledge"
  | "copilot"
  | "sendBuilderMessage"
  | "sendOwnerAssistantMessage"
  | "getOwnerAssistantSuggestions"
  | "runOwnerAssistantAction"
  | "decideBuilderProposal"
  | "listBuilderConversations"
  | "getBuilderConversation"
  | "listAiAgents"
  | "createAiAgent"
  | "copyAiAgent"
  | "getAiMapStats"
  | "getAiAgentTrace"
  | "getAiProfileTemplate"
  | "createAiAgentFromProfileTemplate"
  | "updateAiAgent"
  | "getAiAgentChecklist"
  | "listAiAgentRuns"
  | "getVoiceTranscription"
  | "updateVoiceTranscription"
  | "getReplyRateLimit"
  | "getAiLinkDomains"
  | "updateReplyRateLimit"
  | "publishAiAgent"
  | "unpublishAiAgent"
  | "testAiAgent"
  | "getAiStatus"
  | "getAiProviderModels"
  | "runAiAgentProbes"
  | "getAiEvalCases"
  | "saveAiEvalCases"
  | "runAiEval"
  | "importAiEvalCsv"
  | "generateAiEvalCases"
  | "bindAiAgent"
>;

export function createAiKnowledgeApi({ request, ws }: HttpTransport): AiKnowledgeApiMethods {
  return {
    sendOwnerAssistantMessage: (id, input) =>
      request<OwnerAssistantReply>("POST", ws(id) + "/assistant/messages", input),
    getOwnerAssistantSuggestions: async (id) =>
      (
        await request<{ items: OwnerAssistantSuggestion[] }>(
          "GET",
          ws(id) + "/assistant/suggestions",
        )
      ).items,
    runOwnerAssistantAction: (id, input) =>
      request<OwnerAssistantActionResult>("POST", ws(id) + "/assistant/actions", input),
    sendBuilderMessage: (id, input) =>
      request<BuilderReplyView>("POST", ws(id) + "/copilot/messages", input),
    decideBuilderProposal: (id, proposalId, decision) =>
      request<BuilderProposalView>("POST", ws(id) + `/copilot/proposals/${proposalId}/${decision}`),
    listBuilderConversations: async (id) =>
      (
        await request<{ items: BuilderConversationView[] }>(
          "GET",
          ws(id) + "/copilot/conversations",
        )
      ).items,
    getBuilderConversation: (id, conversationId) =>
      request<BuilderTranscriptView>("GET", ws(id) + `/copilot/conversations/${conversationId}`),
    listKnowledgeSources: (id) => request<KbListView>("GET", ws(id) + "/ai/knowledge-sources"),
    listCustomTools: async (id) =>
      (await request<{ tools: CustomToolView[] }>("GET", ws(id) + "/ai/custom-tools")).tools,
    createCustomTool: async (id, input) =>
      (await request<{ tool: CustomToolView }>("POST", ws(id) + "/ai/custom-tools", input)).tool,
    updateCustomTool: async (id, toolId, input) =>
      (
        await request<{ tool: CustomToolView }>(
          "PATCH",
          ws(id) + "/ai/custom-tools/" + encodeURIComponent(toolId),
          input,
        )
      ).tool,
    deleteCustomTool: (id, toolId) =>
      request<void>("DELETE", ws(id) + "/ai/custom-tools/" + encodeURIComponent(toolId)),
    testCustomTool: (id, toolId, args) =>
      request<CustomToolTestResult>(
        "POST",
        ws(id) + "/ai/custom-tools/" + encodeURIComponent(toolId) + "/test",
        { args },
      ),
    listAgentActions: (id) =>
      request<{ items: { name: string; kind: string }[] }>("GET", ws(id) + "/ai/agents-actions"),
    markMessageWrong: (id, messageId, correctionText) =>
      request<KbFixProposalView>("POST", ws(id) + "/ai/kb-fix-proposals", {
        messageId,
        correctionText,
      }),
    getSmsFallbackSettings: (id) =>
      request<SmsFallbackSettingsView>("GET", `${ws(id)}/settings/channels/sms-fallback`),
    saveSmsFallbackSettings: (id, input) =>
      request<SmsFallbackSettingsView>("PUT", `${ws(id)}/settings/channels/sms-fallback`, input),
    async listByokKeys(id) {
      return (await request<{ keys: ByokKeyView[] }>("GET", `${ws(id)}/settings/ai/byok-keys`))
        .keys;
    },
    async addByokKey(id, provider, apiKey, modelOverride) {
      return (
        await request<{ key: ByokKeyView }>("POST", `${ws(id)}/settings/ai/byok-keys`, {
          provider,
          apiKey,
          ...(modelOverride ? { modelOverride } : {}),
        })
      ).key;
    },
    async testByokKey(id, provider, apiKey) {
      return (
        await request<{ ok: boolean }>("POST", `${ws(id)}/settings/ai/byok-keys/test`, {
          provider,
          apiKey,
        })
      ).ok;
    },
    async setByokModel(id, provider, model) {
      return (
        await request<{ key: ByokKeyView }>(
          "PUT",
          `${ws(id)}/settings/ai/byok-keys/${encodeURIComponent(provider)}/model`,
          { model },
        )
      ).key;
    },
    async rotateByokKey(id, provider, apiKey) {
      return (
        await request<{ key: ByokKeyView }>(
          "POST",
          `${ws(id)}/settings/ai/byok-keys/${encodeURIComponent(provider)}/rotate`,
          { apiKey },
        )
      ).key;
    },
    async revokeByokKey(id, provider) {
      return (
        await request<{ key: ByokKeyView }>(
          "DELETE",
          `${ws(id)}/settings/ai/byok-keys/${encodeURIComponent(provider)}`,
        )
      ).key;
    },
    async listKbFixProposals(id, status) {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      return (
        await request<{ items: KbFixProposalView[] }>("GET", `${ws(id)}/ai/kb-fix-proposals${q}`)
      ).items;
    },
    async listFaqCandidates(id, status) {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      return (
        await request<{ items: FaqCandidateView[] }>(
          "GET",
          `${ws(id)}/ai/faq-mining/candidates${q}`,
        )
      ).items;
    },
    approveFaqCandidate: (id, cid, answer) =>
      request<FaqCandidateView>(
        "POST",
        `${ws(id)}/ai/faq-mining/candidates/${encodeURIComponent(cid)}/approve`,
        answer ? { answer } : {},
      ),
    rejectFaqCandidate: (id, cid) =>
      request<FaqCandidateView>(
        "POST",
        `${ws(id)}/ai/faq-mining/candidates/${encodeURIComponent(cid)}/reject`,
      ),
    getFaqMiningReport: (id) =>
      request<FaqMiningReportView>("GET", `${ws(id)}/ai/faq-mining/report`),
    getWeeklyReports: (id) => request<WeeklyReportList>("GET", `${ws(id)}/ai/weekly-report`),
    saveWeeklyReportSetting: (id, setting) =>
      request<WeeklyReportSetting>("PUT", `${ws(id)}/ai/weekly-report/setting`, setting),
    decideKbFixProposal: (id, proposalId, decision) =>
      request<KbFixProposalView>(
        "POST",
        `${ws(id)}/ai/kb-fix-proposals/${encodeURIComponent(proposalId)}/${decision}`,
      ),
    createKnowledgeSource: (id, input) =>
      request<KbSourceView>("POST", ws(id) + "/ai/knowledge-sources", input),
    async crawlWebsite(id, siteUrl) {
      const res = await request<{ sources: unknown[] }>(
        "POST",
        ws(id) + "/ai/knowledge-sources/sitemap",
        { url: toSitemapUrl(siteUrl) },
      );
      return res.sources.length;
    },
    async uploadKnowledgeFile(id, file) {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      for (let i = 0; i < bytes.length; i += 0x8000)
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return request<KbSourceView>("POST", ws(id) + "/ai/knowledge-sources/upload", {
        filename: file.name,
        contentBase64: btoa(bin),
      });
    },
    reindexKnowledgeSource: (id, sid) =>
      request<KbSourceView>(
        "POST",
        ws(id) + "/ai/knowledge-sources/" + encodeURIComponent(sid) + "/reindex",
      ),
    deleteKnowledgeSource: (id, sid) =>
      request<void>("DELETE", ws(id) + "/ai/knowledge-sources/" + encodeURIComponent(sid)),
    createWebsiteSource: (id, input) =>
      request<KbSourceView>("POST", ws(id) + "/ai/knowledge-sources/website", input),
    listWebsitePages: (id, sid) =>
      request<KbPagesView>(
        "GET",
        ws(id) + "/ai/knowledge-sources/" + encodeURIComponent(sid) + "/pages",
      ),
    recrawlWebsiteSource: (id, sid) =>
      request<KbSourceView>(
        "POST",
        ws(id) + "/ai/knowledge-sources/" + encodeURIComponent(sid) + "/recrawl",
      ),
    updateKnowledgeSource: (id, sid, patch) =>
      request<KbSourceView>(
        "PATCH",
        ws(id) + "/ai/knowledge-sources/" + encodeURIComponent(sid),
        patch,
      ),
    async searchKnowledge(id, query) {
      return (
        await request<{ hits: KbHitView[] }>("POST", ws(id) + "/ai/knowledge/search", { query })
      ).hits;
    },
    copilot: <T>(id: string, action: string, body: Record<string, unknown>) =>
      request<T>("POST", `${ws(id)}/copilot/${action}`, body),
    async listAiAgents(id) {
      return (await request<{ items: AiAgentView[] }>("GET", `${ws(id)}/ai/agents`)).items;
    },
    createAiAgent: (id, input) => request<AiAgentView>("POST", `${ws(id)}/ai/agents`, input),
    copyAiAgent: (id, agentId) =>
      request<AiAgentView>("POST", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/copy`),
    getAiMapStats: (id, range) =>
      request<AiMapStatsView>("GET", `${ws(id)}/ai/agents/map-stats?range=${range}`),
    getAiAgentTrace: async (id, conversationId) =>
      (
        await request<{ steps: AiTraceStep[] }>(
          "GET",
          `${ws(id)}/ai/agents/trace?conversationId=${encodeURIComponent(conversationId)}`,
        )
      ).steps,
    getAiProfileTemplate: (id, locale) =>
      request<AiProfileTemplatePreview>(
        "GET",
        `${ws(id)}/ai/agents/profile-template?locale=${locale}`,
      ),
    createAiAgentFromProfileTemplate: (id, locale) =>
      request<AiAgentView>("POST", `${ws(id)}/ai/agents/from-profile-template`, { locale }),
    updateAiAgent: (id, agentId, patch) =>
      request<AiAgentView>("PATCH", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}`, patch),
    getVoiceTranscription: (id) =>
      request<VoiceTranscriptionSettings>("GET", `${ws(id)}/ai/voice-transcription`),
    updateVoiceTranscription: (id, patch) =>
      request<VoiceTranscriptionSettings>("PUT", `${ws(id)}/ai/voice-transcription`, patch),
    getAiLinkDomains: (id) => request<AiLinkDomains>("GET", `${ws(id)}/ai/link-domains`),
    getReplyRateLimit: (id) =>
      request<ReplyRateLimitSettings>("GET", `${ws(id)}/ai/reply-rate-limit`),
    updateReplyRateLimit: (id, maxTurns) =>
      request<ReplyRateLimitSettings>("PUT", `${ws(id)}/ai/reply-rate-limit`, { maxTurns }),
    getAiAgentChecklist: (id, agentId) =>
      request<AiAgentChecklist>(
        "GET",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/checklist`,
      ),
    listAiAgentRuns: async (id, agentId, limit = 50) =>
      (
        await request<{ items: AiAgentRunView[] }>(
          "GET",
          `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/runs?limit=${limit}`,
        )
      ).items,
    publishAiAgent: (id, agentId) =>
      request<AiAgentView>("POST", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/publish`),
    unpublishAiAgent: (id, agentId) =>
      request<AiAgentView>("POST", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/unpublish`),
    getAiStatus: (id) => request<AiStatusView>("GET", `${ws(id)}/ai/status`),
    getAiProviderModels: (id, provider) =>
      request<AiProviderModelsView>(
        "GET",
        `${ws(id)}/ai/provider-models?provider=${encodeURIComponent(provider)}`,
      ),
    testAiAgent: (id, agentId, messages, channelKind) =>
      request<AiAgentTestReply>(
        "POST",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/test-chat`,
        { messages, ...(channelKind ? { channelKind } : {}) },
      ),
    getAiEvalCases: (id, agentId) =>
      request<AiEvalCasesView>("GET", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/eval`),
    async saveAiEvalCases(id, agentId, cases) {
      await request("PUT", `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/eval`, { cases });
    },
    runAiEval: (id, agentId) =>
      request<AiEvalRunView>(
        "POST",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/eval/run`,
        {},
      ),
    importAiEvalCsv: (id, agentId, csv) =>
      request<AiEvalImportResult>(
        "POST",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/eval/import`,
        { csv },
      ),
    async generateAiEvalCases(id, agentId, count) {
      const res = await request<{ cases: AiEvalCase[] }>(
        "POST",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/eval/generate`,
        count === undefined ? {} : { count },
      );
      return res.cases;
    },
    runAiAgentProbes: (id, agentId, templateKey) =>
      request<AiAgentProbeRun>(
        "POST",
        `${ws(id)}/ai/agents/${encodeURIComponent(agentId)}/probes`,
        { templateKey },
      ),
    async bindAiAgent(id, channelId, agentId) {
      await request("POST", `${ws(id)}/channels/${encodeURIComponent(channelId)}/bind-ai-agent`, {
        agentId,
      });
    },
  };
}
